// Angular
import { FormArray, FormControl, FormGroup } from '@angular/forms';
// Service
import { ObjectUtils } from '@cv-invision/core';
import { CVfUtilsService } from '../../../../../services/cvf-utils.service';
// Model
import {
	SmartTagRoadSubjectMaintainedOptions,
	SmartTagTimeframeOptions, SmartTagViewAffectOptions,
	SmartTagViewQualityOptions
} from './smart-tag-template.model';
import {
	SmartTagFormControlConditionOperationModel,
	SmartTagFormControlModel,
	SmartTagFormControlOutputType,
	SmartTagFormControlType
} from './smart-tag-form-control.model';
import { SmartTagSpaceModel } from './smartTagSpace';

/**
 * To manage any
 */
export abstract class SmartTagTemplateForm {

	// Basic Form
	public innerForm: FormGroup = null;
	// For External Component Updates
	public dropdownInitialValues: any = [];
	// Type of Form Control Type
	public smartTagFormControlType = SmartTagFormControlType;
	// Static Option Values
	public smartTagRoadSubjectMaintainedOptions = SmartTagRoadSubjectMaintainedOptions;
	public smartTagTimeframeOptions = SmartTagTimeframeOptions;
	public smartTagViewQualityOptions = SmartTagViewQualityOptions;
	public smartTagViewAffectOptions = SmartTagViewAffectOptions;

	public formControl: SmartTagFormControlModel[] = [];

	abstract onValuesChange(): void;

	protected constructor(protected cvfUtils: CVfUtilsService) {}

	/**
	 * Get from the selected Smart Tag the possible Form Controls if available
	 * @param selectedSmartTagItem
	 */
	public prepareDynamicSmartTagFormControls(selectedSmartTagItem: any): void {
		let formControl = this.formControl;

		if (selectedSmartTagItem) {
			const newFormControl = this.cvfUtils.getSelectedFormControls(selectedSmartTagItem);

			if (newFormControl !== null) {
				formControl = newFormControl;
			}
		}

		this.formControl = formControl;
	}

	/**
	 * Pre-select the elements to match the init model
	 */
	public initPreloadedDropdowns(dropdowns: Array<any>): void {
		dropdowns.forEach((dropdown: any) => {
			if (dropdown.value && dropdown.value !== null && dropdown.value !== '') {
				const itemValue = dropdown.options.find((option: any) => option.label === dropdown.value);
				if (itemValue) {
					this.dropdownInitialValues[dropdown.field] = itemValue;
				}
			}
		});
	}

	/**
	 * Add different Dynamic Form Controls
	 * @param smartTagFormControlModel
	 * @param smartTagModel
	 */
	public addDynamicFormControl(smartTagFormControlModel: SmartTagFormControlModel, smartTagModel: any): void {
		// Add Text/Number Control
		if (smartTagFormControlModel.control === this.smartTagFormControlType.TEXT
			|| smartTagFormControlModel.control === this.smartTagFormControlType.NUMBER
			|| smartTagFormControlModel.control === this.smartTagFormControlType.BOOL
		) {
			this.innerForm.addControl(smartTagFormControlModel.field, new FormControl(smartTagModel[smartTagFormControlModel.field]));
		} else if (smartTagFormControlModel.control === this.smartTagFormControlType.SINGLE) {
			let dropdownValue = smartTagModel[smartTagFormControlModel.field];

			// Check the outputType, to get the value properly
			if (smartTagFormControlModel?.outputType) {
				switch (smartTagFormControlModel.outputType) {
					case SmartTagFormControlOutputType.ARRAY:
						if (Array.isArray(dropdownValue)) {
							dropdownValue = dropdownValue.length ? dropdownValue[0].typeName : '';
						}
						break;
				}
			}
			// Keep the Value for the Dropdown madness requirements
			this.initPreloadedDropdowns([{
				field: smartTagFormControlModel.field,
				options: smartTagFormControlModel.options,
				value: dropdownValue
			}]);
			// Add Single Control
			this.innerForm.addControl(smartTagFormControlModel.field, new FormControl(dropdownValue));
		} else if (smartTagFormControlModel.control === this.smartTagFormControlType.MULTIPLE) {
			this.addMultiCheckboxes(smartTagFormControlModel.field, smartTagFormControlModel.options, smartTagModel);
		}
	}

	/**
	 * Show or hide based on Conditions
	 * @param formControl
	 */
	public showIf(formControl: SmartTagFormControlModel): boolean {
		let showIf = true;

		if (formControl.condition) {
			const showIfAnd = formControl.condition.and?.every(
				(conditionModel: SmartTagFormControlConditionOperationModel) =>
					this.innerForm.controls[conditionModel.field].value === conditionModel.value
			);

			const showIfOr = formControl.condition.or?.some(
				(conditionModel: SmartTagFormControlConditionOperationModel) =>
					this.innerForm.controls[conditionModel.field].value === conditionModel.value
			);

			const showIfNot = formControl.condition.not?.every(
				(conditionModel: SmartTagFormControlConditionOperationModel) =>
					this.innerForm.controls[conditionModel.field].value != null &&
					this.innerForm.controls[conditionModel.field].value != conditionModel.value
			);

			showIf = showIfNot || (showIfAnd || showIfOr);
		}
		// If Control is hidden, clear the value
		if (!showIf) {
			this.innerForm.controls[formControl.field].patchValue(null);
		}

		return showIf;
	}

	/**
	 * Add Multiselect checkboxes
	 * @param fieldName
	 * @param options
	 * @param smartTagModel
	 * @private
	 */
	private addMultiCheckboxes(fieldName: string, options: any, smartTagModel: any): void {
		this.innerForm.addControl(fieldName, new FormArray([]));
		const checkboxes = <FormArray>this.innerForm.get(fieldName);
		options.forEach((option: any) => {
			const formValue = smartTagModel && smartTagModel[fieldName] ? smartTagModel[fieldName].find((e) => e.typeName === option.value) : false;
			checkboxes.push(new FormControl(formValue));
		});
	}

	/**
	 * Listen to Dropdown Value Changes
	 * @param fieldName
	 * @param event
	 */
	public onDropdownValueChange(fieldName: string, event: any): void {
		const value = ((event && event.label) ? event.label : null);
		this.innerForm.controls[fieldName].patchValue(value);
		this.onValuesChange();
	}

	/**
	 * Process the Object in case is required to do extra work before send the final value
	 * @param smartTagModel
	 * @param tagTemplate
	 */
	public processOutputObject(smartTagModel: any, tagTemplateRenderer: SmartTagSpaceModel = null): any {
		const innerForm = ObjectUtils.recursiveDeepCopy(this.innerForm.value);
		for (const [key] of Object.entries(innerForm)) {
			const propertyValue = innerForm[key];
			const formControlType = this.formControl.find((stfcm: SmartTagFormControlModel) => stfcm.field === key);

			if (Array.isArray(propertyValue)) {
				const options = formControlType.options;
				const newArrayValue = [];
				options.forEach((item, index) => {
					if (propertyValue[index]) {
						newArrayValue.push({ typeName: options[index].value });
					}
				});
				innerForm[key] = newArrayValue;
			}
			// Process other values
			if (tagTemplateRenderer !== null) {
				// If needed
			}

			if (formControlType?.outputType) {
				let outputValue: any;
				switch (formControlType.outputType) {
					case SmartTagFormControlOutputType.ARRAY:
						outputValue = [];
						if (propertyValue !== null) {
							outputValue.push({ typeName: propertyValue });
						}
						innerForm[key] = outputValue;
						break;
				}
			}
		}

		return innerForm;
	}
}
